import type { VenueReservation } from "@/graphql/operations";
import { addMonths, format, startOfMonth } from "date-fns";
import { getReservationTimestamp } from "./reservationTime";
import type {
  MonthKey,
  ReservationSearch,
  ReservationsBlock,
  ReservationsBucket,
} from "./reservationsTypes";

let blockIdCounter = 0;

/**
 * Creates a new empty reservations bucket.
 */
export const createEmptyBucket = (): ReservationsBucket => ({
  blocks: [],
  activeBlockId: null,
  bounds: {
    earliestDate: null,
    latestDate: null,
    resolved: false,
  },
});

/**
 * Builds a stable cache key for a search combination.
 */
export const getBucketKey = ({ venueId, productId }: ReservationSearch) =>
  `${venueId ?? "all"}|${productId ?? "all"}`;

/**
 * Formats a date into a month key used for block IDs.
 */
const toMonthKey = (date: Date): MonthKey => format(date, "yyyy-MM");

/**
 * Creates a reservations block covering the given month range.
 */
export const createBlock = (
  from: Date,
  until: Date,
  records: VenueReservation[],
): ReservationsBlock => {
  const sorted = [...records].sort(
    (left, right) =>
      getReservationTimestamp(left) - getReservationTimestamp(right),
  );
  const start = startOfMonth(from);
  const end = startOfMonth(until);
  const id = `block-${toMonthKey(start)}-${toMonthKey(end)}-${blockIdCounter++}`;
  return {
    id,
    from: start,
    until: end,
    reservations: sorted,
    totalReservations: sorted.length,
    reservationsStart: 0,
    reservationsEnd: sorted.length - 1,
    reachedPastEnd: false,
    reachedUpcomingEnd: false,
  };
};

/**
 * Checks if a month is within a start/end range.
 */
export const isMonthWithinRange = (month: Date, start: Date, end: Date) =>
  month.getTime() >= start.getTime() && month.getTime() <= end.getTime();

/**
 * Checks if two month ranges overlap or touch.
 */
export const rangesOverlapOrAdjacent = (
  start: Date,
  end: Date,
  otherStart: Date,
  otherEnd: Date,
) => {
  const endNext = startOfMonth(addMonths(end, 1));
  const otherEndNext = startOfMonth(addMonths(otherEnd, 1));
  return !(
    otherStart.getTime() > endNext.getTime() ||
    start.getTime() > otherEndNext.getTime()
  );
};

/**
 * Checks if two ranges overlap.
 */
export const rangesOverlap = (
  start: Date,
  end: Date,
  otherStart: Date,
  otherEnd: Date,
) =>
  start.getTime() <= otherEnd.getTime() &&
  end.getTime() >= otherStart.getTime();

/**
 * Merges reservation records, preserving uniqueness and sort order.
 */
const mergeReservationRecords = (
  existing: VenueReservation[],
  records: VenueReservation[],
  position: "start" | "end",
) => {
  if (records.length === 0) {
    return { merged: existing, addedCount: 0 };
  }
  const existingIds = new Set(existing.map((record) => record.id));
  const incomingRecords = records.filter(
    (record) => !existingIds.has(record.id),
  );
  const merged =
    position === "start"
      ? [...incomingRecords, ...existing]
      : [...existing, ...incomingRecords];
  merged.sort(
    (left, right) =>
      getReservationTimestamp(left) - getReservationTimestamp(right),
  );
  return { merged, addedCount: incomingRecords.length };
};

/**
 * Adds records to the start of a block and updates indices.
 */
const prependRecordsToBlock = (
  block: ReservationsBlock,
  records: VenueReservation[],
) => {
  const { merged, addedCount } = mergeReservationRecords(
    block.reservations,
    records,
    "start",
  );
  block.reservations = merged;
  block.totalReservations = merged.length;
  block.reservationsStart -= addedCount;
  block.reservationsEnd = block.reservationsStart + block.totalReservations - 1;
};

/**
 * Adds records to the end of a block and updates indices.
 */
const appendRecordsToBlock = (
  block: ReservationsBlock,
  records: VenueReservation[],
) => {
  const { merged } = mergeReservationRecords(
    block.reservations,
    records,
    "end",
  );
  block.reservations = merged;
  block.totalReservations = merged.length;
  block.reservationsEnd = block.reservationsStart + block.totalReservations - 1;
};

/**
 * Merges all blocks that overlap or are adjacent to the active block.
 */
export const mergeAdjacentBlocks = (
  bucket: ReservationsBucket,
  blockId: string,
) => {
  const block = bucket.blocks.find((item) => item.id === blockId);
  if (!block) return;

  let merged = true;
  while (merged) {
    merged = false;
    for (const candidate of bucket.blocks) {
      if (candidate.id === block.id) continue;
      if (
        !rangesOverlapOrAdjacent(
          block.from,
          block.until,
          candidate.from,
          candidate.until,
        )
      ) {
        continue;
      }
      if (candidate.from.getTime() < block.from.getTime()) {
        prependRecordsToBlock(block, candidate.reservations);
        block.from = candidate.from;
      } else {
        appendRecordsToBlock(block, candidate.reservations);
        block.until = candidate.until;
      }
      bucket.blocks = bucket.blocks.filter((item) => item.id !== candidate.id);
      merged = true;
      break;
    }
  }
};
